<template>
    <div class="automations-page report">
        <loader v-if="is('loading')"></loader>
        <div class="container-fluid" v-if="!is('loading')">
            <div class="row">
                <div class="col-md-12">
                    <h1>{{ automation.title }}</h1>
                    <span class="note"
                        ><strong>{{ t('automations-report-created_at') }}</strong>
                        <human-date :date="automation.created"></human-date
                    ></span>
                </div>
            </div>

            <div class="row stats">
                <div class="col-xs-12 col-md-6 col-lg-3">
                    <div class="box">
                        <div class="info">
                            <div class="number">
                                {{
                                    (!!automation.orders &&
                                        automation.orders.length) ||
                                    0
                                }}
                                <small
                                    >{{ totalClientsReactivated }} {{ t('automations-report-reactivated') }}
                                    <span class="tooltip-inline">
                                        ?
                                        <span class="tooltip-content">
                                            {{ t('automations-report-reactivated_tooltip_part1') }}
                                            <br /><br />
                                            {{ t('automations-report-reactivated_tooltip_part2', [totalClientsReactivated]) }}
                                        </span>
                                    </span>
                                </small>
                            </div>
                            <div class="label">{{ t('automations-report-converted_clients_label') }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-xs-12 col-md-6 col-lg-3" v-if="automation.goal !== 'orders'">
                  <div class="box">
                    <div class="info">
                      <div class="number">
                        {{ automation.conversions }}
                      </div>
                      <div class="label">{{ t('automations-report-conversions_label') }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-xs-12 col-md-6 col-lg-3" v-if="automation.goal === 'orders'">
                    <div class="box">
                        <div class="info">
                            <div class="number">
                                {{ currencySymbol }} {{ formatNumber(totalValue) }}
                            </div>
                            <div class="label">{{ t('automations-report-financial_return_label') }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-xs-12 col-md-6 col-lg-3" v-if="automation.goal === 'orders'">
                    <div class="box">
                        <div class="info">
                            <div class="number">
                                {{ currencySymbol }} {{ formatNumber(avgTicket) }}
                            </div>
                            <div class="label">{{ t('automations-report-avg_ticket_label') }}</div>
                        </div>
                    </div>
                </div>
                <div class="col-xs-12 col-md-6 col-lg-3">
                  <div class="box">
                    <div class="info">
                      <div class="number">
                        {{ formatNumber(returnRate) }}%
                      </div>
                      <div class="label">{{ t('automations-report-conversion_rate_label') }}</div>
                    </div>
                  </div>
                </div>
            </div>

            <div class="row" v-show="maxDaysAfterForOrders && maxDaysAfterForOrders <= 40 && automation.goal === 'orders'">
                <div class="col-sm-12">
                    <div class="box">
                        <bar-chart
                            :chart-data="chartData"
                            :options="chartOptions"
                            :height="200"
                        />
                    </div>
                </div>
            </div>
            <br />
            <div
                class="row grid"
                v-if="!!automation.orders && automation.orders.length > 0"
            >
                <div class="col-sm-12">
                    <h2>{{ t('automations-report-table_title') }}</h2>
                    <table class="table table-stripped table-responsive-md">
                        <thead>
                            <tr>
                                <th>{{ t('automations-report-col_name') }}</th>
                                <th class="col-center">{{ t('automations-report-col_receipt_date') }}</th>
                                <th class="col-center">{{ t('automations-report-col_order_date') }}</th>
                                <th class="col-center">{{ t('automations-report-col_value') }}</th>
                                <th class="col-right">
                                    {{ t('automations-report-col_days_since_prev') }}
                                </th>
                                <th class="col-right">
                                    {{ t('automations-report-col_orders_after') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="order in automation.orders"
                                :key="order.id"
                                style="cursor: pointer"
                                @click="
                                    $router.push(
                                        '/client/' +
                                            order.clients_id +
                                            '#event-' +
                                            order.id,
                                    )
                                "
                            >
                                <td>
                                  <span v-if="order.first_name">
                                    {{ order.first_name }} {{ order.last_name }}
                                  </span>
                                  <span v-else>
                                    {{ t('automations-report-unidentified') }}
                                  </span>
                                </td>
                                <td class="col-center">
                                    <human-date
                                        :date="order.automation_execution_date"
                                        :format="t('dates-format_date')"
                                    />
                                </td>
                                <td class="col-center">
                                    <human-date
                                        :date="order.created"
                                        :format="t('dates-format_date')"
                                    />
                                </td>
                                <td class="col-center">
                                    {{ currencySymbol }}{{ formatNumber(order.value) }}
                                </td>
                                <td class="col-right">
                                    {{ order.days_since_previous_order }}
                                </td>
                                <td class="col-right">
                                    {{ order.orders_after_automation }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import keviApi from "../keviApi";
import HumanDate from "../components/human-date/human-date";

export default {
    props: {
        user: Object,
    },
    components: {
        HumanDate,
    },
    data: function () {
        return {
            state: "ready",
            maxDaysAfterForOrders: null,
            automation: {
                title: null,
                executions: 0,
                created: null,
                orders: [],
                estimatedCost: 0,
                smsSent: 0,
            },
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                tooltips: {
                    callbacks: {
                        label: function (tooltipItems, data) {
                            return parseFloat(
                                tooltipItems.yLabel,
                            ).toLocaleString("pt-BR");
                        },
                    },
                },
                scales: {
                    yAxes: [
                        {
                            ticks: {
                                maxTicksLimit: 6,
                                beginAtZero: true,
                                callback: function (value, index, values) {
                                    return parseFloat(value).toLocaleString();
                                },
                            },
                        },
                    ],
                },
            },
        };
    },
    created: function () {
        this.loadData();
    },
    computed: {
        returnRate() {
            return this.automation.actions
                ? (this.automation.conversions / this.automation.actions) * 100
                : 0;
        },
        totalValue() {
            return this.automation.orders
                ? this.automation.orders.reduce(
                      (a, b) => parseFloat(a) + parseFloat(b.value),
                      0,
                  )
                : 0;
        },
        totalClientsReactivated() {
            return this.automation.orders
                ? this.automation.orders.filter(
                      (o) => o.days_since_previous_order >= 60,
                  ).length
                : 0;
        },
        avgTicket() {
            return this.automation.orders
                ? this.totalValue / this.automation.orders.length
                : 0;
        },
        charts() {
            let chartData = {
                labels: [],
                ordersData: [],
                valueData: [],
            };
            for (let i = 0; i <= this.maxDaysAfterForOrders; i++) {
                chartData.labels.push(i);
                chartData.ordersData.push(
                    this.automation.orders
                        ? this.automation.orders.filter(
                              (order) =>
                                  parseInt(order.days_after_automation) === i,
                          ).length
                        : 0,
                );
                chartData.valueData.push(
                    this.automation.orders
                        ? this.automation.orders
                              .filter(
                                  (order) =>
                                      parseInt(order.days_after_automation) ===
                                      i,
                              )
                              .reduce(
                                  (a, b) => parseFloat(a) + parseFloat(b.value),
                                  0,
                              )
                        : 0,
                );
            }
            chartData.labels = chartData.labels.map((i) => {
                if (i === 0) return this.t('automations-report-chart_same_day');
                if (i === 1) return this.t('automations-report-chart_one_day_after');
                return this.t('automations-report-chart_n_days_after', [i]);
            });
            return chartData;
        },
        chartData() {
            return {
                labels: this.charts.labels,
                datasets: [
                    {
                        label: this.t('automations-report-chart_orders_label'),
                        backgroundColor: "#f87979",
                        data: this.charts.ordersData,
                    },
                ],
            };
        },
    },
    methods: {
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
        is: function (state) {
            return this.state === state;
        },
        setState: function (state) {
            this.state = state;
        },
        calculateAge: function (birthday) {
            birthday = new Date(birthday);
            let ageDifMs = Date.now() - birthday.getTime();
            let ageDate = new Date(ageDifMs); // miliseconds from epoch
            return Math.abs(ageDate.getUTCFullYear() - 1970);
        },
        loadData: function () {
            this.id = null;
            this.$store.commit("automationForm/setActions", []);
            if (this.$route.params.id) {
                this.setState("loading");
                let options = keviApi.getOptions(
                    "automations/" + this.$route.params.id + "/report",
                );
                this.$http(options).then(
                    (response) => {
                        this.setState("ready");
                        let result = response.body;
                        this.automation = result;
                        this.maxDaysAfterForOrders = result.conversion_period;
                    },
                    () => {
                        this.setState("ready");
                    },
                );
            }
        },
        formatNumber(number) {
            return number > 0
                ? parseFloat(number).toLocaleString("pt-BR", {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                  })
                : "0,00";
        },
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
    },
};
</script>
